import React, { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { asTags, formatValue, isbn10, isValidLink, linkDomain } from '../fieldTypes';
import { mediaUrl } from '../config/api';
import { selectLabel, uiLang, useVocabulary, vocabLabels } from '../kinds';
import { Button, StarBar } from '../ui';
import { LinkIcon } from '../ui/icons';
import { sourceId, sourceOf, sourceUrl } from './item/sources';

const STRIP = 3;

/** Codes of a vocabulary as chips of their labels in the UI language. */
function VocabValue({ field, value, lang }) {
  const items = useVocabulary(field.vocabulary);
  const labels = vocabLabels(items, value, lang, field);
  return labels.length ? labels.map((label, i) => <span key={i} className="value-chip">{label}</span>) : '-';
}

/** A number, as the reader writes numbers ("1 112", "7,5"); null when the value is not one. */
const asNumber = (value) => {
  if (value === null || value === undefined || value === '' || typeof value === 'boolean') return null;
  const n = typeof value === 'number' ? value : Number(String(value).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

/** "12 mars 2024" for a full date; a year or anything else as written. */
export const formatDate = (value, lang) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value).trim());
  if (!match) return String(value);
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat(lang, { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(date);
};

const LONG_TEXT = 160;   // characters past which a value may be cut at four lines

/** A long value cut at four lines, with "Show more / Show less" when it is longer. */
export function LongText({ text }) {
  const { t } = useTranslation();
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const [long, setLong] = useState(false);
  useLayoutEffect(() => {
    const node = ref.current;
    if (node && !open) setLong(node.scrollHeight > node.clientHeight + 1);
  }, [text, open]);
  if (text.length <= LONG_TEXT) return text;
  return (
    <span className="value-long">
      <span ref={ref} className={open ? 'value-long__text' : 'value-long__text is-clamped'}>{text}</span>
      {(long || open) && (
        <Button variant="ghost" size="sm" className="value-long__toggle" aria-expanded={open} onClick={() => setOpen(!open)}>
          {open ? t('item.showLess') : t('item.showMore')}
        </Button>
      )}
    </span>
  );
}

/** A text holding a list ("A;B;C", as completion agents wrote names): "A, B, C". */
const listed = (text) => (text.includes(';')
  ? text.split(/\s*;\s*/).map((part) => part.trim()).filter(Boolean).join(', ')
  : text);

/** An id on TMDB or IMDb, as a link to its page (a new tab). */
function SourceLink({ source, id, tmdbKind }) {
  const { t } = useTranslation();
  return (
    <a href={sourceUrl(source, id, tmdbKind)} target="_blank" rel="noopener noreferrer" className="value-source"
       aria-label={t(`item.sourceLink.${source}`, { id })}>
      <span className="value-identifier__scheme">{t(`item.scheme.${source}`)}</span>
      <code className="value-identifier">{id}</code>
      <LinkIcon size={14} />
    </a>
  );
}

/**
 * One value as the item page shows it (spec §1.2): words for yes/no, long
 * dates, money with its currency, a rating as "7,5 / 10" with its stars,
 * identifiers in monospace with their scheme, TMDB and IMDb ids (identifier
 * fields, or the id_tmdb… text fields of older types) as links to their
 * pages (`tmdbKind`: movie or tv), lists written "A;B" as "A, B", long
 * texts cut at four lines.
 */
function DetailValue({ field, value, lang, uiLanguage, tmdbKind }) {
  const { t } = useTranslation();
  const source = sourceOf(field);
  const id = source ? sourceId(source, value) : null;
  // A source without an id-based page (IGDB addresses games by slug) shows the id as a plain identifier.
  if (id && sourceUrl(source, id, tmdbKind)) return <SourceLink source={source} id={id} tmdbKind={tmdbKind} />;
  switch (field.type) {
    case 'vocab':
      return <VocabValue field={field} value={value} lang={uiLanguage} />;
    case 'select':
    case 'radio':
      return String(selectLabel(field, value, uiLanguage) ?? '');
    case 'checkbox':
      return (Array.isArray(value) ? value : [value]).map(v => selectLabel(field, v, uiLanguage)).join(', ');
    case 'tags':
      return <LongText text={asTags(value).join(', ')} />;
    case 'text':
    case 'textarea':
      return <LongText text={listed(String(value))} />;
    case 'yesno':
      if (value === true) return t('fieldInput.yes');
      if (value === false) return t('fieldInput.no');
      return String(value);
    case 'number': {
      const n = asNumber(value);
      return n === null ? String(value) : new Intl.NumberFormat(lang).format(n);
    }
    case 'date':
      return formatDate(value, lang);
    case 'rating': {
      const n = asNumber(value);
      const max = Number(field.max) === 5 ? 5 : 10;
      if (n === null) return String(value);
      return (
        <span className="value-rating">
          <StarBar value={(n * 5) / max} />
          <span>{t('item.ratingOutOf', { value: new Intl.NumberFormat(lang, { maximumFractionDigits: 1 }).format(n), max })}</span>
        </span>
      );
    }
    case 'link':
      return isValidLink(value)
        ? <a href={String(value).trim()} target="_blank" rel="noopener noreferrer" title={String(value)}>{linkDomain(value)} ↗</a>
        : String(value);
    case 'identifier': {
      const scheme = field.scheme || 'other';
      return (
        <span className="value-identifier-line">
          {scheme !== 'other' && <span className="value-identifier__scheme">{t(`item.scheme.${scheme}`)}</span>}
          <code className="value-identifier">{String(value)}</code>
          {/* A 978 ISBN shows its ISBN-10 beside it: computed, never stored (phase 4 §2). */}
          {scheme === 'isbn' && isbn10(value) && (
            <span className="value-identifier__also">
              <span className="value-identifier__scheme">ISBN-10</span> <code className="value-identifier">{isbn10(value)}</code>
            </span>
          )}
        </span>
      );
    }
    default:
      return formatValue(field, value, lang) ?? '';
  }
}

/**
 * One value, drawn for its field type; '-' when empty. In the items table
 * by default (compact: ✓ for yes, chips for tags); `variant="detail"` is
 * the item page's (FieldDisplay), with words and long forms.
 */
function FieldValue({ field, value, variant = 'table', tmdbKind }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || i18n.language || 'en';
  const empty = value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0);
  if (empty) return '-';
  if (variant === 'detail') {
    return <DetailValue field={field} value={value} lang={lang} uiLanguage={uiLang(i18n)} tmdbKind={tmdbKind} />;
  }

  switch (field.type) {
    case 'vocab':
      return <VocabValue field={field} value={value} lang={uiLang(i18n)} />;
    case 'select':
      return String(selectLabel(field, value, uiLang(i18n)) ?? '-');
    case 'tags': {
      const tags = asTags(value);
      return tags.length ? tags.map((tag, i) => <span key={i} className="value-chip">{tag}</span>) : '-';
    }
    case 'link':
      return isValidLink(value)
        ? <a href={String(value).trim()} target="_blank" rel="noopener noreferrer" title={String(value)}>↗ {linkDomain(value)}</a>
        : String(value);
    case 'yesno':
      if (value === true) return <span title={t('fieldInput.yes')}><span aria-hidden="true">✓</span><span className="visually-hidden">{t('fieldInput.yes')}</span></span>;
      if (value === false) return <span title={t('fieldInput.no')}><span aria-hidden="true">—</span><span className="visually-hidden">{t('fieldInput.no')}</span></span>;
      return String(value);
    case 'image': {
      // A few small thumbnails, then how many more.
      const ids = Array.isArray(value) ? value.filter(id => Number.isInteger(id)) : [];
      if (ids.length === 0) return '-';
      return (
        <span className="value-strip" title={t('imageInput.count', { count: ids.length })}>
          {ids.slice(0, STRIP).map(id => <img key={id} src={mediaUrl(id, 'thumb')} alt="" loading="lazy" />)}
          {ids.length > STRIP && <span className="value-strip__more" aria-hidden="true">+{ids.length - STRIP}</span>}
          <span className="visually-hidden">
            {t('imageInput.count', { count: ids.length })}
          </span>
        </span>
      );
    }
    case 'identifier':
      return <code>{String(value)}</code>;
    default:
      return formatValue(field, value, lang) ?? '-';
  }
}

/** The item page's form of FieldValue (one component, so the table and the page agree). */
export const FieldDisplay = (props) => <FieldValue {...props} variant="detail" />;

export default FieldValue;
